import type React from "react"
import { LayoutDashboard, ChevronRight, Plus } from "lucide-react"

type CrudListHeaderProps = {
  title: string
  icon: React.ComponentType<{ className?: string }>
  onAdd?: () => void
  addLabel?: string
  actions?: React.ReactNode
  mobileActions?: React.ReactNode
  hideStickyAdd?: boolean
}

export function CrudListHeader({
  title,
  icon: Icon,
  onAdd,
  addLabel,
  actions,
  mobileActions,
  hideStickyAdd = false,
}: CrudListHeaderProps) {
  const label = addLabel ?? `Add ${title}`

  return (
    <>
      <div className="mb-2 hidden items-center text-xs text-slate-gray md:flex">
        <LayoutDashboard className="mr-1 h-3.5 w-3.5" />
        <span>Dashboard</span>
        <ChevronRight className="mx-1 h-3.5 w-3.5" />
        <span className="font-medium text-deep-ocean">{title}</span>
      </div>

      <div className="mb-3 rounded-xl border border-slate-gray/10 bg-white p-3 shadow-sm sm:p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <h1 className="flex items-center gap-2 text-lg font-semibold text-deep-ocean sm:text-xl">
            <Icon className="h-5 w-5 shrink-0 text-electric-blue" />
            {title}
          </h1>
          {(actions || onAdd) && (
            <div className="hidden flex-wrap gap-2 md:flex">
              {actions}
              {onAdd ? (
                <button
                  type="button"
                  onClick={onAdd}
                  className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-electric-blue px-3 text-sm font-medium text-white hover:bg-btn-hover"
                >
                  <Plus className="h-4 w-4" />
                  {label}
                </button>
              ) : null}
            </div>
          )}
        </div>
        {mobileActions ? (
          <div className="mt-3 grid grid-cols-3 gap-2 border-t border-slate-gray/10 pt-3 md:hidden">
            {mobileActions}
          </div>
        ) : null}
      </div>

      {onAdd && !hideStickyAdd ? (
        <button
          type="button"
          onClick={onAdd}
          className="fixed bottom-20 right-4 z-30 flex h-12 w-12 items-center justify-center rounded-full bg-electric-blue text-white shadow-lg hover:bg-btn-hover md:hidden"
          aria-label={label}
        >
          <Plus className="h-5 w-5" />
        </button>
      ) : null}
    </>
  )
}
